import { useState, useRef, useEffect } from 'react';
import { X, Zap, MessageCircle, Share2, Gift, ImageIcon, Mic } from 'lucide-react';
import { timeAgo } from '../../../utils/timeAgo';
import { formatTextWithHashtags } from '../../../utils/formatText';
import LazyImage from '../../LazyImage';
import MediaViewer from '../../../components/postcard/MediaViewer';
import { supabase } from '../../../lib/supabase';

// Extend Window interface to include gtag for Google Analytics
declare global {
  interface Window {
    gtag?: (command: string, targetId: string, config?: any) => void;
  }
}

interface Comment {
  id: string;
  content: string;
  created_at: string;
  users?: {
    username: string;
    avatar_url: string;
    verified: boolean;
  };
  content_id: string;
  impressions: number;
  updated_at: string;
  is_pinned?: boolean;
  is_ad: boolean;
}

interface User {
  username: string;
  avatar_url: string;
  verified: boolean;
}

interface Post {
  id: string;
  caption?: string;
  media_url: string;
  media_type?: 'image' | 'video' | 'music';
  additional_media_urls?: string[];
  mediaItems?: {
    id: string;
    url: string;
    type: 'image' | 'video' | 'music';
    thumbnail?: string;
  }[];
  music_info?: {
    title: string;
    artist: string;
    coverArt: string;
    duration: number;
  };
  created_at: string;
  users: User;
  likes_count: number;
  comments_count: number;
  bookmarks_count: number;
  gifts_count: number;
  views_count: number;
  comments: Comment[];
}

interface SmallDevicePostViewProps {
  post: Post;
  isOpen: boolean;
  onClose?: () => void;
  initialMediaIndex?: number;
}

export default function SmallDevicePostView({ post, isOpen, onClose, initialMediaIndex = 0 }: SmallDevicePostViewProps) {
  const [comment, setComment] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [comments, setComments] = useState<Comment[]>(post.comments || []);
  
  const commentInputRef = useRef<HTMLInputElement>(null);
  const commentsRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  
  const handleLike = async () => {
    setIsLiked(!isLiked);
    // You could add actual like functionality here
  };
  
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Post by ${post.users.username}`,
        text: post.caption || 'Check out this post!',
        url: window.location.href
      }).catch(err => console.error('Error sharing:', err));
    } else {
      // Fallback for browsers that don't support Web Share API
      console.log('Web Share API not supported');
    }
  };
  
  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;
    
    try {
      const { data, error } = await supabase
        .from('comments')
        .insert([
          { 
            content: comment, 
            content_id: post.id,
            is_pinned: false,
            is_ad: false
          }
        ]);
        
      if (error) throw error;
      
      if (data) {
        // Add the new comment to the list
        setComments([...comments, data[0]]);
        setComment('');
        
        // Scroll to the bottom of comments
        if (commentsRef.current) {
          commentsRef.current.scrollTop = commentsRef.current.scrollHeight;
        }
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };
  
  // Handle media view tracking for analytics and engagement
  const handleMediaView = async (mediaId: string) => {
    try {
      // Track media view for analytics
      // This could be connected to services like Google Analytics, Mixpanel, etc.
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'media_view', {
          event_category: 'engagement',
          event_label: `post_${post.id}_media_${mediaId}`,
          value: 1
        });
      }

      // Update local view count (optimistic update)
      // In a real app, this would trigger an API call to increment view count
      console.log(`Media ${mediaId} viewed for post ${post.id}`);
      
      // Example API call (commented out for demo):
      // await updateMediaViewCount(post.id, mediaId);
      
    } catch (error) {
      console.error('Error tracking media view:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-900 w-full h-full md:h-auto md:max-h-[90vh] md:max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto flex flex-col rounded-none md:rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-3">
            <LazyImage
              src={post.users.avatar_url}
              alt={post.users.username}
              className="w-10 h-10 rounded-full"
            />
            <div>
              <h3 className="font-semibold">{post.users.username}</h3>
              <p className="text-sm text-gray-500">{timeAgo(post.created_at)}</p>
            </div>
          </div>
          <button
            onClick={() => onClose?.()}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Media */}
        <div className="bg-black flex items-center justify-center">
          <MediaViewer
            media_url={post.mediaItems?.[0]?.url || post.media_url || ''}
            caption={post.caption || ''}
            mediaType={post.media_type || 'image'}
            className="w-full h-full"
            onMediaView={handleMediaView}
            additionalMediaUrls={post.mediaItems?.slice(1).map(item => item.url) || post.additional_media_urls || []}
            musicInfo={post.music_info}
            initialMediaIndex={initialMediaIndex}
            postId={post.id}
            postOwnerId={post.users.id}
          />
        </div>

        {/* Caption */}
        {post.caption && (
          <div className="p-4 border-b">
            <p className="text-gray-900 dark:text-gray-100">
              {formatTextWithHashtags(post.caption)}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex space-x-4">
              <button
                onClick={handleLike}
                className={`${isLiked ? 'text-yellow-500' : 'text-gray-600'} hover:text-yellow-500`}
              >
                <Zap className={`w-6 h-6 ${isLiked ? 'fill-current' : ''}`} />
              </button>
              <button className="text-gray-600 hover:text-gray-900">
                <MessageCircle className="w-6 h-6" />
                <span className="ml-1 text-sm">{comments.length}</span>
              </button>
              <button onClick={handleShare} className="text-gray-600 hover:text-gray-900">
                <Share2 className="w-6 h-6" />
              </button>
            </div>
            <div className="flex space-x-4">
              <button className="text-gray-600 hover:text-gray-900">
                <Gift className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Comments */}
        <div className="flex-1 overflow-y-auto" ref={commentsRef}>
          <div className="p-4 md:p-6 space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="flex space-x-3">
                <LazyImage
                  src={comment.users?.avatar_url || ''}
                  alt={comment.users?.username || ''}
                  className="w-8 h-8 rounded-full flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-sm">
                      {comment.users?.username}
                    </span>
                    <span className="text-xs text-gray-500">
                      {timeAgo(comment.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                    {formatTextWithHashtags(comment.content)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Comment Input */}
        <div className="p-4 md:p-6 border-t bg-white dark:bg-gray-900">
          <form onSubmit={handleSubmitComment} className="flex items-center space-x-3">
            <LazyImage
              src={post.users.avatar_url}
              alt="Your avatar"
              className="w-8 h-8 rounded-full flex-shrink-0"
            />
            <div className="flex-1 flex items-center bg-gray-100 dark:bg-gray-800 rounded-full px-4 py-2 relative">
              <button type="button" className="text-gray-400 hover:text-gray-600 mr-2">
                <ImageIcon className="w-5 h-5" />
              </button>
              <button type="button" className="text-gray-400 hover:text-gray-600 mr-2">
                <Mic className="w-5 h-5" />
              </button>
              <input
                ref={commentInputRef}
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 bg-transparent outline-none text-sm placeholder-gray-500"
              />
            </div>
            <button
              type="submit"
              disabled={!comment.trim()}  
              className="bg-[#065900] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#054700] transition-colors disabled:opacity-50"
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
